import { ApiService } from '@/apiConnection/ApiService';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { CompanyBusinessTypeService } from '@/apiConnection/company-business-type.service';
import { Urls } from '@/apiConnection/urls';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';
import { LoadingComponent } from '@/components/loading/loading.component';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { Utils } from '@/utils/utils';
import { Component, Input, Output, EventEmitter, ChangeDetectorRef, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

// Must match erp-backend's CompaniesController validation (`business_type` => 'in:restaurant,store,other').
const BUSINESS_TYPES = [
  { id: 'restaurant', name: 'Restaurante' },
  { id: 'store', name: 'Tienda' },
  { id: 'other', name: 'Otro' },
];

@Component({
  selector: 'app-company-form',
  imports: [
    CustomModalComponent,
    ReactiveFormsModule,
    CustomInputComponent,
    CustomSelectComponent,
    LoadingComponent,
  ],
  templateUrl: './company-form.component.html',
  styleUrl: './company-form.component.scss',
})
export class CompanyFormComponent {
  @Input() id: number | string = 0;
  @Output() closeFunction = new EventEmitter<any>();
  @Output() acceptFunction = new EventEmitter<any>();

  public loading = signal(false);
  public saving = signal(false);

  public saveForm: FormGroup = new FormGroup({});

  public showModalImage = false

  public dropdowns: any = {}

  public showOptionalFields = false;

  public businessTypes = BUSINESS_TYPES;

  private codeRetryCount = 0;

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private authService: AuthenticationService,
    private businessTypeService: CompanyBusinessTypeService
  ) { }

  get isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin;
  }

  ngOnInit(): void {
    this.createForm();

    if (this.id != 0) {
      this.getById(this.id);
    }
  }

  acceptFormFunction() {
    this.saveEntity()
  }

  closeFormFunction() {
    this.closeFunction.emit();
  }

  createForm() {
    this.saveForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      cif: new FormControl('', [Validators.required, Validators.maxLength(9), Validators.minLength(9)]),
      email: new FormControl(null, [Validators.email]), // Opcional
      is_active: new FormControl(true),
      business_type: new FormControl(null), // Opcional
      invoices_prefix: new FormControl(null), // Opcional
      code: new FormControl(null), // Opcional
      address: new FormControl(null), // Opcional
      province: new FormControl(null), // Opcional
      city: new FormControl(null), // Opcional
      postal_code: new FormControl(null), // Opcional
      max_users: new FormControl(null, [Validators.min(1)]), // Opcional
      tariffs: new FormArray([]), // Opcional
    })
  }

  get tariffs(): FormArray {
    return this.saveForm.get('tariffs') as FormArray;
  }

  toggleAdditionalPrices() {
    const priceGroup = new FormGroup({
      id: new FormControl(this.tariffs.length + 1),
      rate: new FormControl(null),
      price: new FormControl(0),
      vat: new FormControl(false),
    });

    this.tariffs.push(priceGroup);
  }

  toggleOptionalFields() {
    this.showOptionalFields = !this.showOptionalFields;
  }

  getById(id: number | string) {
    this.loading.set(true);
    this.apiService.getById(Urls.urls.companies, id).subscribe({
      next: (data: any) => {
        this.saveForm.patchValue(data.company);
        this.loading.set(false);

        this.showOptionalFields = true;

        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('ERROR obtener productos. ' + error);
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  saveEntity() {
    if (this.saveForm.valid) {
      this.saving.set(true);

      this.apiService.save(Urls.urls.companies, this.saveForm.value, this.id).subscribe({
        next: (response: any) => {
          this.codeRetryCount = 0;
          // business_type may have changed: the sidebar filters menus on it
          this.businessTypeService.load(true);
          this.acceptFunction.emit(response);
          // Utils.ToastUtils.success('Producto guardado', 'El producto ha sido guardado correctamente.');
          this.closeFormFunction()
          // Utils.showSuccessMessage('Producto guardado correctamente');
        },
        error: (error: any) => {
          if (Utils.isCodeAlreadyTakenError(error) && this.codeRetryCount < Utils.MAX_CODE_ALREADY_TAKEN_RETRIES) {
            this.codeRetryCount++;
            this.saveForm.get('code')?.setValue(Utils.incrementCode(this.saveForm.get('code')?.value));
            this.saveEntity();
            return;
          }

          this.codeRetryCount = 0;
          this.saving.set(false);
          Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al guardar la empresa');
        },
        complete: () => {
          this.saving.set(false);
        }
      })
    } else {
      Utils.notification.alert('Debe completar todos los campos obligatorios');
    }
  }

}
